// Pure helpers of the subscription screens (BILL-FE-01) and of the state banner (BILL-FE-02): what
// the subscription says about the organisation, in the words of the banner, and the display of
// amounts and sizes. Amounts stay integer strings of millimes until they are formatted.
import type { SubscriptionDtoOutput } from '@batiflow/api-client';
import { money, type Money } from '@batiflow/domain-fiscal';
import { intlLocale, type AppLocale } from '@batiflow/i18n';

export type Subscription = SubscriptionDtoOutput;
export type SubscriptionStatus = Subscription['status'];

/** Where the subscription page lives, relative to the organisation. */
export const SUBSCRIPTION_PATH = '/o/$orgId/admin/subscription' as const;

/** The trial banner shows from this many days before the end of the trial. */
export const TRIAL_BANNER_DAYS = 7;

/** A renewal can be asked from this many days before the end of the period. */
export const RENEW_WINDOW_DAYS = 30;

/** The most months of pause in a calendar year (BILLING_RULES.pauseMaxMonthsPerYear). */
export const PAUSE_MAX_MONTHS_PER_YEAR = 3;

const DAY_MS = 86_400_000;

/** TND amount from an integer string of millimes (never a float). */
export const tnd = (minor: string): Money => money(BigInt(minor), 'TND');

/** Whole days from `now` to `at`, a started day counting as a day left; never negative. */
export function daysUntil(at: string | Date, now: Date): number {
  const ms = new Date(at).getTime() - now.getTime();
  return Math.max(0, Math.ceil(ms / DAY_MS));
}

const TB = 1024 ** 4;
const GB = 1024 ** 3;
const MB = 1024 ** 2;

/** "100 Go" (fr), "1 To", "1.5 TB" (en): a size from an integer string of bytes. */
export function formatStorage(bytes: string, locale: AppLocale): string {
  const value = Number(bytes);
  const unit =
    value >= TB ? 'terabyte' : value >= GB ? 'gigabyte' : value >= MB ? 'megabyte' : 'byte';
  const factor = unit === 'terabyte' ? TB : unit === 'gigabyte' ? GB : unit === 'megabyte' ? MB : 1;
  return new Intl.NumberFormat(intlLocale(locale), {
    style: 'unit',
    unit,
    unitDisplay: 'short',
    maximumFractionDigits: unit === 'byte' ? 0 : 1,
  }).format(value / factor);
}

/** A plain count in the language of the interface (Latin digits in Arabic). */
export const formatCount = (value: number | string, locale: AppLocale): string =>
  new Intl.NumberFormat(intlLocale(locale)).format(Number(value));

/** Share of the limit in use, rounded down (99.9 % must not read 100); 100 once it is reached. */
export function usagePercent(used: string, limit: string): number {
  const max = BigInt(limit);
  const current = BigInt(used);
  if (max <= 0n) return current > 0n ? 100 : 0;
  if (current >= max) return 100;
  return Number((current * 100n) / max);
}

export type StatusTone = 'success' | 'warning' | 'danger' | 'informative' | 'neutral' | 'brand';

/** Tone of the status chip (the label and the icon always accompany the colour). */
export const STATUS_TONE: Readonly<Record<SubscriptionStatus, StatusTone>> = {
  trialing: 'informative',
  active: 'success',
  past_due: 'warning',
  read_only: 'danger',
  suspended: 'danger',
  paused: 'neutral',
  cancelled: 'neutral',
};

export type OrganizationStatus = 'trial' | 'active' | 'read_only' | 'suspended' | 'closed';

/** Writes are refused while the organisation is read-only or suspended (ORGANIZATION_READ_ONLY). */
export const isLockedStatus = (status: string | undefined): status is 'read_only' | 'suspended' =>
  status === 'read_only' || status === 'suspended';

/** The states a banner explains, one at a time (the most serious wins). */
export type BannerKind =
  | 'suspended'
  | 'read_only_trial'
  | 'read_only_unpaid'
  | 'past_due'
  | 'paused'
  | 'cancelling'
  | 'cancelled'
  | 'trial_ending';

export type BannerIntent = 'error' | 'warning' | 'info';

export interface BannerState {
  kind: BannerKind;
  intent: BannerIntent;
  /** The date the sentence names: since (past due), until (paused), end (cancelling, trial). */
  date: string | null;
  /** Days left, for the trial. */
  days: number | null;
  /** An invoice waits for its payment (the call to action says "pay"). */
  hasOpenInvoice: boolean;
}

/**
 * What to tell the whole organisation. `organizationStatus` (from /me) is known to every member;
 * `subscription` only to those who may read billing and refines the sentence. Null: nothing to say.
 */
export function bannerState(
  organizationStatus: string | undefined,
  subscription: Subscription | undefined,
  now: Date,
): BannerState | null {
  const hasOpenInvoice = (subscription?.openInvoice ?? null) !== null;
  const status = subscription?.status;

  if (organizationStatus === 'suspended' || status === 'suspended')
    return { kind: 'suspended', intent: 'error', date: null, days: null, hasOpenInvoice };

  // Paused and cancelled organisations are read-only too: say why.
  if (status === 'paused')
    return {
      kind: 'paused',
      intent: 'info',
      date: subscription?.pausedUntil ?? null,
      days: null,
      hasOpenInvoice,
    };
  if (status === 'cancelled')
    return {
      kind: 'cancelled',
      intent: 'error',
      date: subscription?.cancelAt ?? null,
      days: null,
      hasOpenInvoice,
    };

  if (organizationStatus === 'read_only' || status === 'read_only') {
    // The trial ended without a plan: no paid period was ever due (the server keeps due_at empty).
    const trialEnded = subscription?.dueAt === null && subscription.trialEndsAt !== null;
    return {
      kind: trialEnded ? 'read_only_trial' : 'read_only_unpaid',
      intent: 'error',
      date: subscription?.dueAt ?? null,
      days: null,
      hasOpenInvoice,
    };
  }

  if (!subscription) return null;

  if (subscription.status === 'past_due')
    return {
      kind: 'past_due',
      intent: 'warning',
      date: subscription.dueAt,
      days: null,
      hasOpenInvoice,
    };

  if (subscription.status === 'active' && subscription.cancelAt !== null)
    return {
      kind: 'cancelling',
      intent: 'warning',
      date: subscription.cancelAt,
      days: null,
      hasOpenInvoice,
    };

  if (subscription.status === 'trialing' && subscription.trialEndsAt !== null) {
    const days = daysUntil(subscription.trialEndsAt, now);
    if (days <= TRIAL_BANNER_DAYS)
      return {
        kind: 'trial_ending',
        intent: 'warning',
        date: subscription.trialEndsAt,
        days,
        hasOpenInvoice,
      };
  }
  return null;
}

/** The subscription's own idea of "writes are refused", to compare with /me. */
export const subscriptionLocksWrites = (status: SubscriptionStatus): boolean =>
  status === 'read_only' || status === 'suspended' || status === 'cancelled';
